import { CloseOutlined } from "@ant-design/icons";
import { Button, Modal, Progress, Spin, Typography } from "antd";
import Cookies from "js-cookie";
import { useEffect, useRef, useState } from "react";
import apiFactory from "../../../api";
import {
  CHUNK_SIZE,
  FILE_STATUS,
  MESSAGE_STATUS,
} from "../../../config/Constant";
import { useInfoUser } from "../../../store/UserStore";
import { openNotificationError } from "../../../utils/Utils";
import "./style.scss";

const getBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });

const PreviewUploadModal = ({
  isModalOpen,
  handleCancel,
  setDataForm,
  uploadFile,
  setPreviewFile,
  setIsOpenUploadModal,
}) => {
  const { languageMap } = useInfoUser();
  const [abortController, setAbortController] = useState({});
  const sharedAbortController = useRef(null);
  const fileUploading = useRef(null);
  const [progressState, setProgressState] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);

  const sendFile = async () => {
    const controller = new AbortController();
    sharedAbortController.current = controller;

    try {
      fileUploading.current = uploadFile;

      const rs = await handleSendFile(uploadFile);

      if (controller.signal.aborted) {
        throw new Error("Aborted");
      }

      if (rs) {
        setDataForm({
          originalName: uploadFile?.name,
          pathUrl: rs?.path,
          resourceId: rs?.resourceId,
          volume: rs?.volume,
          duration: rs?.duration,
          uid: uploadFile?.uid,
        });

        setPreviewFile(uploadFile);
      }

      fileUploading.current = null;
    } catch (error) {}
  };

  const handleDeleteFile = (uid) => {
    if (uploadFile && abortController) {
      abortController.abort();
      apiFactory.messageApi.cancelMessage({
        requestUuid: uid,
      });
    }
  };

  const handleSendFile = async (file) => {
    try {
      if (!file) return null;

      const totalChunk = Math.ceil(file.size / CHUNK_SIZE);
      const payload = {
        requestUuid: file.uid,
        contentType: MESSAGE_STATUS.FILE,
        name: file.name,
        token: Cookies.get("access_token"),
        totalVolume: file.size,
      };

      const controller = new AbortController();
      setAbortController(controller);

      let totalChunkUploaded = 0;

      for (let i = 0; i < totalChunk; i++) {
        if (controller.signal.aborted) {
          throw new Error("Aborted");
        }

        const chunk = file.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE);
        const base64Chunk = await getBase64(chunk);

        const item = {
          ...payload,
          // requestUuid: file?.uid,
          base64Chunk,
          totalChunk,
          chunkIndex: i,
          status: FILE_STATUS.READY,
        };

        let retryCount = 0;
        let success = false;

        while (retryCount < 3 && !success) {
          const message = await sendFileChunk(item);

          if (message) {
            success = true;
            totalChunkUploaded++;
            updateProgress(file, totalChunkUploaded, totalChunk);
          } else {
            retryCount++;
            console.error(
              `Retry ${retryCount} for chunk ${i} of file ${file.name}`
            );
          }
        }

        if (!success) {
          console.error(`Failed to upload chunk ${i} of file ${file.name}`);
          return;
        }
      }

      if (totalChunkUploaded === totalChunk) {
        const payloadMerge = {
          ...payload,
          totalChunk: totalChunk,
          folder: "PUBLIC",
        };

        const rs = await mergeChunks(payloadMerge);

        return rs;
      }

      return null;
    } catch (err) {
      console.log(err);
    }
  };

  const sendFileChunk = async (item) => {
    try {
      const res = await apiFactory.resourceApi.uploadChunk({
        folder: "PUBLIC",
        base64: item?.base64Chunk,
        chunkIndex: item?.chunkIndex,
        requestUuid: item?.requestUuid,
      });

      if (res?.status === 400) {
        openNotificationError(
          languageMap?.[res?.message] || "Upload failed, try again later"
        );
        return null;
      }

      return {
        ...res?.data,
        fileName: item?.name,
        totalChunk: item?.totalChunk,
        type: "PUBLIC",
      };
    } catch (error) {
      console.error("Error uploading chunk:", error);
      return null;
    }
  };

  const mergeChunks = async (payload) => {
    try {
      const rs = await apiFactory.messageApi.mergeChunk(payload);

      if (rs?.status === 200) {
        fileUploading.current.isSuccess = true;
        return rs?.data;
      } else {
        fileUploading.current.isSuccess = false;
        return null;
      }
    } catch (error) {
      console.error("Error merging chunks:", error);
      return null;
    }
  };

  const updateProgress = (file, uploaded, total) => {
    const percent = Math.round((uploaded / total) * 100);
    setProgressState((prev) => ({
      ...prev,
      [file.uid]: percent,
    }));
  };

  const getFileSize = (file) => {
    if (!file?.size) return "0 KB";

    const sizeInMB = file.size / (1024 * 1024);
    const sizeInKB = file.size / 1024;

    return sizeInMB >= 1
      ? `${sizeInMB.toFixed(1)} MB`
      : `${sizeInKB.toFixed(1)} KB`;
  };

  const handleCloseModal = () => {
    handleStopAllUploads();
    handleCancel();
    setProgressState({});
  };

  const handleStopAllUploads = () => {
    if (sharedAbortController.current && abortController) {
      abortController.abort();
      sharedAbortController.current.abort();
    }
  };

  useEffect(() => {
    if (uploadFile !== null && !isUploading) {
      setIsLoading(false);
      setIsUploading(true);
      sendFile().finally(() => {
        setIsUploading(false);

        setTimeout(() => {
          // handleCancel();
          setIsOpenUploadModal(false);
        }, 1000);
      });
    }
  }, [uploadFile]);

  return (
    <Modal
      title="Upload"
      open={isModalOpen}
      centered
      closable={false}
      maskClosable={false}
      footer={
        isUploading && (
          <div style={{ marginTop: 20, textAlign: "right" }}>
            <Button onClick={handleCloseModal}>Hủy</Button>
          </div>
        )
      }
    >
      <Typography.Title level={5}>Đang đính kèm tệp</Typography.Title>
      {!isLoading ? (
        <div key={uploadFile?.uid}>
          <div style={{ marginTop: 20 }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                marginTop: 10,
              }}
            >
              <Typography.Text>{uploadFile?.name}</Typography.Text>
              {fileUploading.current?.uid === uploadFile?.uid && (
                <CloseOutlined
                  style={{ marginLeft: 10, cursor: "pointer" }}
                  onClick={() => handleDeleteFile(uploadFile?.uid)}
                />
              )}
              <Typography.Text style={{ marginLeft: "auto" }}>
                {getFileSize(uploadFile)}
              </Typography.Text>
            </div>

            <Progress percent={progressState[uploadFile?.uid] || 0} />
          </div>
        </div>
      ) : (
        <div className="w-full h-full items-center justify-center pt-5 pb-5">
          <Spin />
        </div>
      )}
    </Modal>
  );
};

export { PreviewUploadModal };
